import * as vp from 'vopay';
import {
  Box,
  Title,
  Table as ExportTable,
  Button,
  Pagination,
  Tooltip,
  Icon,
  formattedCurrency,
  Card,
  MaterialSymbol,
  usePagination,
  useUpdateQueryParams,
  TransactionTypesOptions,
} from 'vopay';
import type { TransactionHistoryStatus } from 'vopay';
import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { format, subWeeks, isAfter, parseISO, getTime } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useFileDownload from '@/hooks/useFiledDownload';
import usePostConfirmTransaction from '@/hooks/useApi/usePostConfirmTransaction';
import useFetchTransactionsFile from '@/hooks/useApi/useFetchTransactionsFile';
import { useClientData } from '@/providers/ClientData.provider';
import TransactionSearchForm from './TransactionSearchForm';
import styles from './Transactions.module.scss';
import ConfirmTransactionDialogue from '../Dialogue/ConfirmTransaction/ConfirmTransactionDialogue';
import { useUserSettingsProvider } from '@/providers/UserSettings.provider';

import { ConfirmTransactionDialogueHandler, DialogueHandlerProps } from '../Dialogue/types';
import CopyButton from '@/components/UI/CopyButton/CopyButton';
import TransactionStatus from '@/components/UI/TransactionStatus/TransactionStatus';
import Truncate from '@/components/UI/Truncate';
import TransactionsExport from './TransactionsExport';

import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { TableFiltersContainer, Table } from 'UI';
import useTransactionHistoryExport from './useTransactionHistoryExport';
import { useDataFetchIndicator } from '@/hooks';
import useAccountBalances from '@/hooks/useApi/useAccountBalances';

type QueryParams = {
  type: string;
  status: TransactionHistoryStatus | null;
  flagged: string;
  currency: string;
  searchTerm: string;
  startDate: string;
  endDate: string;
  timeZone: string;
  settlementDate: string | null;
  includeSubAccounts: string;
};

type FC = FunctionComponent;

const TransactionsHistory: FC = () => {
  const { userSettings } = useUserSettingsProvider();
  const { t } = useTranslation('translations', { keyPrefix: 'Transaction.TransactionHistory.TransactionHistory' });
  const { t: tGlobal } = useTranslation('translations');
  const { downloadPdf, downloadCsv, downloadJson, downloadXls, downloadDoc, downloadTxt, downloadXml, printFile } =
    useFileDownload();
  const [filteredList, setFilteredList] = useState<vp.TransactionsLists[]>([]);

  const {
    response: { Country },
  } = useClientData();

  const location = useLocation();

  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const currentTimeZone = useMemo(
    () => (queryParams.get('timeZone') as vp.Timezone) || userSettings.timezone || 'UTC',
    [queryParams],
  );
  const settlementDate = useMemo(() => queryParams.get('settlementDate') || null, [queryParams]);
  const type = useMemo(() => queryParams.get('type') || '', [queryParams]);
  const status = useMemo(() => (queryParams.get('status') as TransactionHistoryStatus) || null, [queryParams]);
  const flagged = useMemo(() => queryParams.get('flagged') || '', [queryParams]);
  const { balances } = useAccountBalances();
  const currencies = useMemo(() => balances.map((balance) => balance.Currency), [balances]);
  const currency = useMemo(() => queryParams.get('currency') || currencies[0], [queryParams, currencies]);
  const searchTerm = useMemo(() => queryParams.get('searchTerm') || '', [queryParams]);
  const startDate = useMemo(() => queryParams.get('startDate') || '', [queryParams]);
  const endDate = useMemo(() => queryParams.get('endDate') || '', [queryParams]);

  const currentDateTime = useMemo(() => formatInTimeZone(new Date(), currentTimeZone, 'yyyy-MM-dd'), [currentTimeZone]);
  const timeDifference = useMemo(
    () => formatInTimeZone(subWeeks(new Date(), 1), currentTimeZone, 'yyyy-MM-dd'),
    [currentTimeZone],
  );

  const filtersApplied = useMemo(() => {
    return Boolean(
      type ||
        status ||
        flagged ||
        searchTerm ||
        (currency && currency !== (Country === 'US' ? 'USD' : 'CAD')) ||
        (currentTimeZone && currentTimeZone !== (userSettings.timezone || 'UTC')) ||
        (startDate && startDate !== timeDifference) ||
        (endDate && endDate !== currentDateTime),
    );
  }, [
    type,
    status,
    flagged,
    searchTerm,
    currency,
    Country,
    currentTimeZone,
    userSettings.timezone,
    startDate,
    endDate,
    timeDifference,
    currentDateTime,
  ]);

  const includeSubAccounts = useMemo(() => {
    const includeSubAccountsParam = queryParams.get('includeSubAccounts');
    return includeSubAccountsParam === 'true';
  }, [queryParams]);

  const updateQueryParams = useUpdateQueryParams<QueryParams>();
  const [transactionId, setTransactionId] = useState('');
  const [initialLoad, setInitialLoad] = useState(false);
  const [shouldShowEndDateError, setShouldShowEndDateError] = useState(false);
  const [, setLoading] = useDataFetchIndicator();
  const [transactionHistory, fetchTransactions] = useTransactionHistoryExport();
  const [transactionsHistoryExport, getTransactionsHistoryExport] = useTransactionHistoryExport();
  const [, , , postConfirmAsync] = usePostConfirmTransaction({});
  const [transactionFileResponse, getTransactionsFile] = useFetchTransactionsFile({});
  const [hasInvalidEndDate, setHasInvalidEndDate] = useState(false);

  const alertDialog = useAlertDialog();
  // pagination
  const totalDataLength = useMemo(() => {
    return transactionHistory.PaginationMetaData?.TotalRecords || 0;
  }, [transactionHistory.Transactions]);
  const PER_PAGE = 20;
  const { pageCount, pageIndex, setPageIndex } = usePagination(totalDataLength, {
    pageSize: PER_PAGE,
  });

  const [showConfirmTransactionErrorAlert, setShowConfirmTransactionErrorAlert] = useState(false);
  const [sendConfirmTransactionError, setSendConfirmTransactionError] = useState('');
  const [showConfirmTransaction, setShowConfirmTransaction] = useState(false);
  const [flaggedMessage, setFlaggedMessage] = useState('');

  const handlers = {
    handleCancelTransactionDialogue: (handlerProps: DialogueHandlerProps<ConfirmTransactionDialogueHandler>) => {
      const { type: dialogType, state, action = 'default' } = handlerProps;

      const dialogueHandlers: ConfirmTransactionDialogueHandler = {
        error: {
          success: {
            default: () => {
              setShowConfirmTransactionErrorAlert(false);
            },
          },
          fail: {
            default: () => {
              setShowConfirmTransactionErrorAlert(false);
            },
          },
        },
        confirm: {
          success: {
            default: async () => {
              const confirmTransactionResponse = await postConfirmAsync({
                TransactionID: transactionId,
              });
              const { Success, ErrorMessage } = confirmTransactionResponse;
              if (Success === true) {
                fetchTransactionList({
                  startDate,
                  endDate,
                  status,
                  flagged,
                  type,
                  currency,
                  hasInvalidEndDate: startDate > endDate,
                  searchTerm,
                  timeZone: currentTimeZone,
                  includeSubAccounts,
                });
              } else {
                setShowConfirmTransactionErrorAlert(true);
                setSendConfirmTransactionError(ErrorMessage);
              }
              setShowConfirmTransaction(false);
            },
          },
          fail: {
            default: () => {
              setShowConfirmTransaction(false);
            },
          },
        },
      };
      const dialogueState = dialogueHandlers[dialogType][state];
      const handler = dialogueState[action] || dialogueState.default;

      handler();
    },
  };

  useEffect(() => {
    // Compare as dates, not strings
    const start = parseISO(startDate);
    const end = parseISO(endDate);
    if (isAfter(start, end)) {
      setHasInvalidEndDate(true);
    } else {
      setHasInvalidEndDate(false);
    }
  }, [startDate, endDate]);

  type TransactionParams = {
    startDate: string;
    endDate: string;
    status: TransactionHistoryStatus | 'All' | null;
    flagged: string;
    type: string;
    currency: string;
    hasInvalidEndDate: boolean;
    searchTerm: string;
    timeZone: string;
    offset?: number;
    includeSubAccounts: boolean;
    settlementDate?: string;
  };

  const fetchTransactionList = async ({
    startDate: selectedStartDate,
    endDate: selectedEndDate,
    status: selectedStatus,
    flagged: isFlaggedSelected,
    type: selectedType,
    currency: selectedCurrency,
    hasInvalidEndDate: isEndDateInvalid,
    searchTerm: searchQuery,
    timeZone,
    offset = 0,
    includeSubAccounts,
    settlementDate,
  }: TransactionParams) => {
    const transactionRequestPayload = {
      StartDateTime: selectedStartDate,
      EndDateTime: selectedEndDate,
      TransactionStatus: selectedStatus === null || selectedStatus === 'All' ? undefined : selectedStatus,
      IsFlagged: isFlaggedSelected === 'All' || isFlaggedSelected === '' ? undefined : isFlaggedSelected === 'Yes',
      TransactionType:
        selectedType === 'All' ||
        selectedType === '' ||
        selectedType === 'Refund' ||
        selectedType === 'Auto Balance Transfer'
          ? undefined
          : selectedType,
      Currency: selectedCurrency,
      SearchText: searchQuery,
      Timezone: timeZone,
      SettlementDate: settlementDate,
      Limit: PER_PAGE,
      Offset: offset,
      IncludeSubaccountTransactions: includeSubAccounts,
    };

    if (isEndDateInvalid) {
      setShouldShowEndDateError(true);
      return;
    }

    try {
      setLoading(true);
      const res = await fetchTransactions(transactionRequestPayload);

      if (!res.Success) throw new Error(res.ErrorMessage);

      const TransactionsRequests = Object.values(res.Transactions || {});

      setFilteredList(
        TransactionsRequests.map((transaction) => ({
          ...transaction,
          TransactionType: ModifyType(transaction.TransactionType, transaction.Notes),
        })),
      );

      setInitialLoad(true);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'An error occurred while fetching eLinx history';
      alertDialog.error('Error', msg);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (balances.length === 0) return;
    const formattedStartDate = startDate ? format(parseISO(startDate), 'yyyy-MM-dd') : timeDifference;
    const formattedEndDate = endDate ? format(parseISO(endDate), 'yyyy-MM-dd') : currentDateTime;
    const formattedSettlementDate = settlementDate ? format(parseISO(settlementDate), 'yyyy-MM-dd') : null;

    updateQueryParams({
      type,
      status,
      flagged,
      currency,
      searchTerm,
      includeSubAccounts: includeSubAccounts ? 'true' : 'false',
      startDate: formattedStartDate,
      endDate: formattedEndDate,
      timeZone: currentTimeZone,
      settlementDate: formattedSettlementDate,
    });

    fetchTransactionList({
      startDate: formattedStartDate,
      endDate: formattedEndDate,
      status,
      flagged,
      type,
      currency,
      hasInvalidEndDate: isAfter(parseISO(startDate), parseISO(endDate)),
      searchTerm,
      includeSubAccounts,
      timeZone: currentTimeZone,
      settlementDate: formattedSettlementDate || undefined,
    });
  }, [balances]);

  useEffect(() => {
    const { Success, CSVFile, PDFFile } = transactionFileResponse;

    if (Success === true) {
      if (PDFFile) {
        downloadPdf(PDFFile, 'transactions');
      } else if (CSVFile) {
        downloadCsv(CSVFile, 'transactions');
      }
    }
  }, [transactionFileResponse]);

  const handleExport = async (exportType: 'pdf' | 'csv' | 'xls' | 'doc' | 'text' | 'json' | 'xml' | 'print') => {
    const exportOptions = {
      StartDateTime: startDate,
      EndDateTime: endDate,
      Timezone: currentTimeZone,
      TransactionStatus: status === null || status === 'All' ? undefined : status,
      IsFlagged: flagged === 'All' || flagged === '' ? undefined : flagged === 'Yes',
      TransactionType:
        type === 'All' || type === '' || type === 'Refund' || type === 'Auto Balance Transfer' ? undefined : type,
      Currency: currency,
      SearchText: searchTerm,
      ExportParams: {
        IncludeSubAccounts: includeSubAccounts,
      },
      IncludeSubaccountTransactions: includeSubAccounts,
    };

    const exportHandlers: Record<typeof exportType, () => unknown> = {
      pdf: () => getTransactionsFile({ ...exportOptions, ExportPDF: true }),
      csv: () => getTransactionsFile({ ...exportOptions, ExportCSV: true }),
      xls: async () => {
        await getTransactionsHistoryExport({ ...exportOptions });
        downloadXls('export-transaction-history-table', 'transactions');
      },
      doc: async () => {
        await getTransactionsHistoryExport({ ...exportOptions });
        downloadDoc('export-transaction-history-table', 'transactions');
      },
      text: async () => {
        await getTransactionsHistoryExport({ ...exportOptions });
        downloadTxt('export-transaction-history-table', 'transactions');
      },
      json: async () => {
        const exportTransactionData = await getTransactionsHistoryExport({ ...exportOptions });
        if (exportTransactionData) downloadJson({ Transactions: exportTransactionData.Transactions }, 'transactions');
      },
      xml: async () => {
        await getTransactionsHistoryExport({ ...exportOptions });
        downloadXml('export-transaction-history-table', 'transactions');
      },
      print: async () => {
        await getTransactionsHistoryExport({ ...exportOptions });
        printFile('export-transaction-history-table');
      },
    };

    try {
      setLoading(true);
      await exportHandlers[exportType]();
    } finally {
      setLoading(false);
    }
  };

  const tableProps: vp.TableProps<vp.TransactionsLists> = {
    id: 'transactionHistoryTable',

    version: '2',
    zebra: true,
    columns: [
      {
        id: 'transactionDate',
        header: t('table.transactionDate'),
        align: 'left',
        style: {
          whiteSpace: 'nowrap',
          minWidth: '100px',
        },
        template: (transaction) => {
          return (
            <Box gap="sm" ai="center">
              {transaction.data.TransactionFlag !== '' && (
                <Tooltip
                  information={transaction.data.TransactionFlag}
                  hasButton={false}
                  customElement={
                    <Button
                      color="none"
                      background="transparent"
                      label={<MaterialSymbol fillAmount={1} icon="flag" size="24px" color="rgba(189, 58, 45, 1)" />}
                      noPadding
                      noShadow
                      onClick={() => {
                        setTransactionId(transaction.data.TransactionID);
                        setShowConfirmTransaction(true);
                        setFlaggedMessage(transaction.data.TransactionFlag);
                      }}
                    />
                  }
                  position="right"
                />
              )}
              <span>{transaction.data.TransactionDateTime}</span>
            </Box>
          );
        },
      },
      {
        id: 'transId',
        header: t('table.id'),
        align: 'left',
        style: {
          minWidth: '100px',
        },

        template: ({ data }) => {
          return (
            <Box gap="sm" ai="center">
              <Table.Link
                to={{
                  pathname: `/transactions-history/details/${vp.removeWhiteSpaceURI(data.TransactionType)}/${
                    data.TransactionID
                  }/${data.Currency}`,
                  state: {
                    transactionId: data.TransactionID,
                    hasErrorInfo: data.TransactionStatus === 'failed',
                    failureReason: data.TransactionFailureReason,
                    accountId: data.ClientAccountID,
                  },
                }}
              >
                {data.TransactionID}
              </Table.Link>
              <CopyButton content={data.TransactionID} color="rgba(45, 45, 45, 0.6)" />
            </Box>
          );
        },
      },
      {
        id: 'accountName',
        header: t('table.accountName'),
        align: 'left',
        field: 'AccountName',
        visible: includeSubAccounts,
      },
      {
        id: 'type',
        header: t('table.type'),
        align: 'left',
        template: (transaction) => {
          return tGlobal(`Transaction.TransactionHistory.transactionTypes.${transaction.data.TransactionType}`, {
            defaultValue: transaction.data.TransactionType,
          });
        },
        style: {
          minWidth: '100px',
        },
      },
      {
        id: 'paymentRailID',
        header: t('table.paymentRailID', { defaultValue: 'Payment Rail ID' }),
        align: 'left',
        field: 'PaymentRailID',
        style: {
          minWidth: '100px',
        },
      },
      {
        id: 'name',
        header: t('table.name'),
        align: 'left',
        field: 'FullName',
        style: {
          minWidth: '100px',
        },
      },

      {
        id: 'referenceNumber',
        header: t('table.referenceNumber'),
        align: 'left',
        field: 'ClientReferenceNumber',
        style: {
          minWidth: '100px',
        },
      },
      {
        id: 'credit',
        header: t('table.credit'),
        align: 'left',
        style: {
          whiteSpace: 'nowrap',
          minWidth: '100px',
        },
        template: (transaction) => {
          return (
            <>
              {transaction.data.CreditAmount !== '0.00' && (
                <Box flx="col" gapSize="6px">
                  <span>{`$${formattedCurrency(transaction.data.CreditAmount)}`}</span>
                  {transaction.data.HoldAmount !== '0.00' && (
                    <span className={styles.pendingAmount}>
                      {t('table.pending')} {`$${formattedCurrency(transaction.data.HoldAmount)}`}
                    </span>
                  )}
                </Box>
              )}
            </>
          );
        },
      },
      {
        id: 'debit',
        header: t('table.debit'),
        align: 'left',
        style: {
          whiteSpace: 'nowrap',
          minWidth: '100px',
        },
        template: (transaction) => {
          return (
            <>
              {transaction.data.DebitAmount !== '0.00' && (
                <Box flx="col" gapSize="6px">
                  <span>{`$${formattedCurrency(transaction.data.DebitAmount)}`}</span>
                  {transaction.data.HoldAmount !== '0.00' && (
                    <span className={styles.pendingAmount}>
                      {t('table.pending')} {`$${formattedCurrency(transaction.data.HoldAmount)}`}
                    </span>
                  )}
                </Box>
              )}
            </>
          );
        },
      },
      {
        id: 'status',
        header: t('table.status'),
        align: 'left',
        style: {
          whiteSpace: 'nowrap',
          minWidth: '100px',
        },
        template: (transaction) => {
          return (
            <Box gapSize="10px">
              <TransactionStatus
                status={transaction.data.TransactionStatus}
                label={
                  vp.capitalizeFirstLetter(
                    tGlobal(`Transaction.TransactionHistory.statuses.${transaction.data.TransactionStatus}`, {
                      defaultValue: transaction.data.TransactionStatus,
                    }),
                  ) || ''
                }
              />
              {transaction.data.TransactionFailureReason && (
                <Tooltip
                  information={transaction.data.TransactionFailureReason}
                  icon="info"
                  variant="outlined"
                  iconWidth="14px"
                  buttonWidth="22px"
                  hasButton={true}
                  position="bottomLeft"
                />
              )}
            </Box>
          );
        },
      },
      {
        id: 'settlementDate',
        header: t('table.settlementDate'),
        align: 'left',
        field: 'SettlementDate',
        style: {
          minWidth: '100px',
        },
      },
      {
        id: 'notes',
        header: t('table.notes'),
        align: 'left',
        style: {
          minWidth: '220px',
        },
        template: (transaction) => {
          const isTruncate = transaction.data.Notes.length > 25;

          return (
            <>
              <Tooltip
                information={transaction.data.Notes}
                isDisabled={!isTruncate}
                hasButton={false}
                customElement={
                  <Box gap="sm" ai="center" jc="sb" className="notes">
                    <Truncate text={transaction.data.Notes} maxLength={25} truncateEnding="..." />
                    {transaction.data.Notes && (
                      <CopyButton content={transaction.data.Notes} color="rgba(45, 45, 45, 0.6)" />
                    )}
                  </Box>
                }
                position="bottomLeft"
              />
            </>
          );
        },
      },
    ],
    rows:
      filteredList.map((transaction): { id: string; data: vp.TransactionsLists } => ({
        id: transaction.TransactionID,
        data: transaction,
      })) || [],
  };

  const exportTableProps: vp.TableProps = {
    id: 'export-transaction-history-table',
    name: 'transactions',
    columns: {
      id: { header: t('table.id'), align: 'left' },
      name: { header: t('table.name'), align: 'left' },
      transactionDate: { header: t('table.transactionDate'), align: 'left' },
      type: { header: t('table.type') || '', align: 'left' },
      referenceNumber: { header: t('table.referenceNumber'), align: 'left' },
      amount: { header: t('table.amount') },
      pending: { header: t('table.pending') },
      flag: { header: t('table.flag') },
      status: { header: t('table.status') },
      failureReason: { header: t('table.failureReason') },
      notes: { header: t('table.notes') },
    },
    rows:
      transactionsHistoryExport.Transactions.map((transaction, index) => ({
        id: transaction.TransactionID,
        name: transaction.FullName,
        transactionDate: transaction.TransactionDateTime,
        type: tGlobal(`Transaction.TransactionHistory.transactionTypes.${transaction.TransactionType}`, {
          defaultValue: transaction.TransactionType,
        }),
        referenceNumber: transaction.ClientReferenceNumber,
        amount: `$${
          transaction.CreditAmount === '0.00'
            ? formattedCurrency(transaction.DebitAmount)
            : formattedCurrency(transaction.CreditAmount)
        }`,
        pending: `$${formattedCurrency(transaction.HoldAmount)}`,
        flag: (
          <div className={styles.flagSection}>
            <span>{transaction.TransactionFlag} </span>
            {transaction.TransactionFlag !== '' ? (
              <Button
                id={`remove-flag-${index}`}
                label={t('table.removeFlag')}
                onClick={() => {
                  setTransactionId(transaction.TransactionID);
                }}
              />
            ) : (
              <></>
            )}
          </div>
        ),
        status:
          vp.capitalizeFirstLetter(
            tGlobal(`Transaction.TransactionHistory.statuses.${transaction.TransactionStatus}`, {
              defaultValue: transaction.TransactionStatus,
            }),
          ) || '',
        failureReason: transaction.TransactionFailureReason,
        notes: transaction.Notes,
      })) || [],
  };

  const EmptyOrNoResults = () => {
    const isEmpty = initialLoad && tableProps.rows?.length === 0;
    const isNoSearch = !initialLoad;

    const message = isNoSearch ? t('transactions.searchPrompt') : t('transactions.noRecords');

    return isEmpty || isNoSearch ? (
      <Box flx="col" className={styles.transactionTableContainer} width="100%" gap="common">
        <Box flx="col" jc="center" gap="sm">
          {isNoSearch && (
            <Box ai="center" jc="center" height="auto">
              <Icon icon="search2" size="lg" />
            </Box>
          )}
          <Box ai="center" jc="center" pt="md" height="auto">
            {message}
          </Box>
        </Box>
      </Box>
    ) : null;
  };

  return (
    <>
      {balances.length !== 0 && (
        <Box flx="row" jc="center">
          <Box flx="col" gapSize="36px" width="100%">
            <Box jc="sb" ai="center" width="100%">
              <Title title={t('transactions.title')} />
              <TransactionsExport onExport={handleExport} />
            </Box>
            <Card
              style={{
                padding: '16px',
              }}
            >
              <Box flx="col" gap="sm">
                <TableFiltersContainer filtersApplied={filtersApplied}>
                  <TableFiltersContainer.Trigger />
                  <TableFiltersContainer.Content>
                    <TransactionSearchForm
                      type={type}
                      status={status}
                      flagged={flagged}
                      currency={currency}
                      searchTerm={searchTerm}
                      timeZone={currentTimeZone}
                      balances={balances}
                      includeSubAccounts={includeSubAccounts}
                      settlementDate={
                        (settlementDate ? getTime(parseISO(settlementDate)) : undefined) as vp.Timestamp | undefined
                      }
                      initialTimeRange={[
                        getTime(startDate ? parseISO(startDate) : parseISO(timeDifference)) as vp.Timestamp,
                        getTime(endDate ? parseISO(endDate) : parseISO(currentDateTime)) as vp.Timestamp,
                      ]}
                      hasInvalidEndDate={hasInvalidEndDate}
                      shouldShowEndDateError={shouldShowEndDateError}
                      setShouldShowEndDateError={setShouldShowEndDateError}
                      onSearch={(searchParams) => {
                        const offset = setPageIndex(0);
                        const formattedStartDate = format(new Date(searchParams.timeRange[0]), 'yyyy-MM-dd');
                        const formattedEndDate = format(new Date(searchParams.timeRange[1]), 'yyyy-MM-dd');
                        const formattedSettlementDate = searchParams.settlementDate
                          ? format(new Date(searchParams.settlementDate), 'yyyy-MM-dd')
                          : undefined;
                        updateQueryParams({
                          type: searchParams.type,
                          status: searchParams.status,
                          flagged: searchParams.flagged,
                          currency: searchParams.currency,
                          searchTerm: searchParams.searchTerm,
                          startDate: formattedStartDate,
                          endDate: formattedEndDate,
                          timeZone: searchParams.timeZone,
                          settlementDate: formattedSettlementDate,
                          includeSubAccounts: searchParams.includeSubAccounts ? 'true' : 'false',
                        });
                        fetchTransactionList({
                          startDate: formattedStartDate,
                          endDate: formattedEndDate,
                          status: searchParams.status,
                          flagged: searchParams.flagged,
                          type: searchParams.type,
                          currency: searchParams.currency,
                          hasInvalidEndDate: isAfter(
                            new Date(searchParams.timeRange[0]),
                            new Date(searchParams.timeRange[1]),
                          ),
                          settlementDate: formattedSettlementDate,
                          searchTerm: searchParams.searchTerm,
                          timeZone: searchParams.timeZone,
                          offset: offset,
                          includeSubAccounts: searchParams.includeSubAccounts,
                        });
                      }}
                    />
                  </TableFiltersContainer.Content>
                </TableFiltersContainer>
              </Box>
              {exportTableProps.rows.length < 7000 && (
                <div className={styles.tableContainer} style={{ display: 'none' }} id="exportTransactionTable">
                  <ExportTable {...exportTableProps} />
                </div>
              )}

              <Box>
                <Table {...tableProps} />
              </Box>
              <EmptyOrNoResults />

              <Box width="100%" jc="center" mt="lg" mb="sm">
                {pageCount > 1 && (
                  <Pagination
                    pageCount={pageCount}
                    forcePageNumber={pageIndex}
                    handlePageClick={(e) => {
                      const offset = setPageIndex(e.selected);
                      fetchTransactionList({
                        startDate,
                        endDate,
                        status,
                        flagged,
                        type,
                        currency,

                        hasInvalidEndDate: startDate > endDate,
                        searchTerm,
                        timeZone: currentTimeZone,
                        offset,
                        includeSubAccounts,
                      });
                    }}
                  />
                )}
              </Box>
            </Card>
          </Box>
        </Box>
      )}
      <ConfirmTransactionDialogue
        handleDialogue={handlers.handleCancelTransactionDialogue}
        showConfirmTransactionErrorAlert={showConfirmTransactionErrorAlert}
        sendConfirmTransactionError={sendConfirmTransactionError}
        flaggedMessage={flaggedMessage}
        showConfirmTransaction={showConfirmTransaction}
      />
    </>
  );
};
function ModifyType(transactionType: TransactionTypesOptions, notes: string): TransactionTypesOptions {
  if (
    (transactionType === 'EFT Withdrawal' || transactionType === 'ACH Withdrawal') &&
    notes === 'Scheduled Auto Balance Transfer'
  ) {
    return 'Auto Balance Transfer';
  }
  return transactionType;
}

export default TransactionsHistory;
